import type { InjectionKey, Ref } from "vue";

/* Which Review tab is showing, so the shell can pick the right measure.
 *
 * The measure is drawn per SCREEN, not per route, and the Review family is the
 * one place where that distinction bites. Only the page view is drawn wide:
 *
 *   composer.html:34      .wrap{max-width:1240px}   the page view, 1192px
 *   share-view.html:39    .wrap{max-width:1240px}   the share view, 1192px
 *   page-status.html:26   .wrap{max-width:1000px}   the contact sheet, 952px
 *   feedback-view.html:28 .wrap{max-width:1000px}   the feedback list, 952px
 *
 * All three of those Review screens are one route (`review`), so a measure
 * chosen from `route.name` alone made the contact sheet and the feedback list
 * 1192px too. That is invisible in a 1000px screenshot, because both measures
 * collapse to the same gutter below their own width, and plainly wrong on a real
 * monitor: at 1400px the sheet laid out nine columns of chips where the design
 * draws seven, which reads as a smaller-typed, denser screen.
 *
 * AppShell cannot simply read this off the route, because the tab lives in
 * ReviewView's own state (it is a hash, not a route param). It also cannot be
 * set by the view through a CSS variable: `--pad` is resolved on `.shell__body`
 * from `--measure`, so a descendant setting `--measure` would be read too late.
 * So the shell owns the ref and the view writes to it.
 *
 * AppShell gates on the route as well, so a tab left on `page` by a screen that
 * has since been navigated away from cannot widen an unrelated route.
 */
export type ReviewTab = "page" | "sheet" | "feedback" | "compare";

export const reviewTabKey: InjectionKey<Ref<ReviewTab>> = Symbol("reviewTab");
